"use client";

/* eslint-disable react/prop-types */
import { useEffect, useMemo, useState } from "react";
import { X, ChevronDown } from "lucide-react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useMenuConfig } from "@kkeut/ui/hooks";
import { collection, getDocs } from "firebase/firestore";
import { db } from "@kkeut/lib/firebase/client";

const MENU_STATE = {
  ko: { loading: "메뉴를 불러오는 중입니다.", error: "메뉴를 불러오지 못했습니다.", retry: "다시 시도", empty: "표시할 메뉴가 없습니다." },
  en: { loading: "Loading menu…", error: "Could not load the menu.", retry: "Try again", empty: "No menu items are available." },
  ja: { loading: "メニューを読み込んでいます…", error: "メニューを読み込めませんでした。", retry: "再試行", empty: "表示できるメニューがありません。" },
  zh: { loading: "正在加载菜单…", error: "无法加载菜单。", retry: "重试", empty: "暂无可显示的菜单。" },
  th: { loading: "กำลังโหลดเมนู…", error: "ไม่สามารถโหลดเมนูได้", retry: "ลองอีกครั้ง", empty: "ไม่มีเมนูที่จะแสดง" },
};

const isExternal = (href = "") =>
  /^https?:\/\//.test(href) || href.startsWith("tel:") || href.startsWith("mailto:");

const normHref = (href = "/") => (href.startsWith("/") ? href : `/${href}`);

const stripLeadingLocale = (path = "/") =>
  (path || "/").replace(/^\/(ko|en|ja|zh|th)(?=\/|$)/, "") || "/";

/* 시술메뉴 펼침 — 그 지점의 시술 카테고리를 관리자 순서대로 (시술메뉴 페이지와 같은 데이터) */
const PRICE_UI = {
  ko: { all: "전체보기", toggle: "시술 카테고리 펼치기" },
  en: { all: "View All", toggle: "Show treatment categories" },
  ja: { all: "すべて", toggle: "施術カテゴリを表示" },
  zh: { all: "全部", toggle: "展开疗程分类" },
  th: { all: "ทั้งหมด", toggle: "แสดงหมวดหมู่ทรีตเมนต์" },
};
const pickText = (v, locale) =>
  v == null ? "" : typeof v === "string" ? v : v[locale] ?? v.ko ?? Object.values(v).find(Boolean) ?? "";
const isPriceMenu = (href = "") => /^\/(main\/)?clinicPrice\/?$/.test(stripLeadingLocale(normHref(href)).split("?")[0]);

async function loadPriceCategories(branchId) {
  const snap = await getDocs(collection(db, branchId, "categories", "list"));
  return snap.docs
    .map((d) => ({ id: d.id, ...(d.data() || {}) }))
    .filter((d) => d.order !== undefined)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
}

const NEED_LANG_PREFIX = [
  "/reserve", "/remoteReserve", "/reserveChange",
  "/cart", "/care",
  "/clinicPrice", "/skinNote", "/blog", "/rss",
  "/before", "/about/kkeut",
];

function defaultWithLocaleHref(href = "/", locale = "ko") {
  if (isExternal(href)) return href;
  let path = normHref(href || "/");
  if (path.startsWith("/admin")) return path;

  path = stripLeadingLocale(path);
  // 외국어일 때 /main/X → /X (아래에서 /{lang} 을 붙인다). /en/main/X 는 404 — 공용 locale-url 과 같은 규칙
  if (locale && locale !== "ko") path = path.replace(/^\/main(?=[/?#]|$)/, "") || "/";
  const url = new URL("http://x" + path);
  const base = url.pathname;
  const needLang = NEED_LANG_PREFIX.some((p) => base === p || base.startsWith(p + "/"));
  if (needLang) url.searchParams.set("lang", locale);

  const pure = url.pathname + (url.search ? url.search : "");
  return locale && locale !== "ko" ? `/${locale}${pure === "/" ? "" : pure}` : (pure || "/");
}

function defaultLabelOf(label, locale) {
  if (typeof label === "string") return label;
  return label?.[locale] ?? label?.ko ?? "";
}

function defaultIsActiveForLocale(item, locale) {
  // 기본 동작: locales 필드가 없거나, 빈 배열이거나, 현재 locale 포함이면 활성
  if (!item) return false;
  const locales = item?.locales;
  if (!locales || (Array.isArray(locales) && locales.length === 0)) return true;
  if (Array.isArray(locales)) return locales.includes(locale);
  return true;
}

/**
 * 모바일 햄버거 메뉴 사이드바.
 *
 * 사용 방식:
 *   - `branchId` 와 `sidebarState` 만 주면 Firestore 메뉴 로딩 + 열림/닫힘 처리
 *   - `initialMenuItems` 직접 주입 가능 (Storybook/테스트/SSR)
 *
 * @param {object} props
 * @param {string} [props.branchId]
 * @param {Array} [props.initialMenuItems]
 * @param {object} props.sidebarState - useMobileSidebarState() 결과
 *   { isOpen, toggle, setIsOpen }
 * @param {string} [props.locale="ko"]
 * @param {function} [props.withLocaleHref]
 * @param {function} [props.labelOf]
 * @param {function} [props.isActiveForLocale]
 */
export function MobileSidebar({
  branchId,
  initialMenuItems = null,
  sidebarState,
  locale = "ko",
  withLocaleHref = defaultWithLocaleHref,
  labelOf = defaultLabelOf,
  isActiveForLocale = defaultIsActiveForLocale,
}) {
  const { isOpen, toggle, setIsOpen } = sidebarState || { isOpen: false, toggle: () => {}, setIsOpen: () => {} };
  const pathname = usePathname();
  const [isDropdown, setIsDropdown] = useState("");
  const hasInitialMenu = Array.isArray(initialMenuItems) && initialMenuItems.length > 0;
  const { menuItems: fetchedMenuItems, loading, error, reload } = useMenuConfig(hasInitialMenu ? null : branchId);
  const menuItems = hasInitialMenu ? initialMenuItems : fetchedMenuItems;
  const ui = MENU_STATE[locale] || MENU_STATE.ko;
  const isLoading = !hasInitialMenu && loading;
  const loadError = !hasInitialMenu && error;

  useEffect(() => {
    setIsDropdown("");
    setIsOpen(false);
  }, [pathname, setIsOpen]);

  const handleDropdown = (id) => setIsDropdown((prev) => (prev === id ? "" : id));

  // 시술메뉴 ⌄ 를 처음 펼칠 때만 카테고리를 읽는다 (메뉴를 열기만 해서는 읽지 않는다)
  const [priceCats, setPriceCats] = useState(null);
  const [priceCatsFailed, setPriceCatsFailed] = useState(false);
  const loadPriceCats = () => {
    if (!branchId || priceCats) return;
    setPriceCatsFailed(false);
    loadPriceCategories(branchId).then(setPriceCats).catch(() => setPriceCatsFailed(true));
  };
  const priceUi = PRICE_UI[locale] || PRICE_UI.ko;
  const closeSidebar = () => setIsOpen(false);

  const visibleItems = useMemo(
    () => menuItems.filter((m) => isActiveForLocale(m, locale)),
    [menuItems, locale, isActiveForLocale],
  );

  const rootItems = useMemo(() => visibleItems.filter((item) => !item.parent), [visibleItems]);
  const childItems = useMemo(() => visibleItems.filter((item) => item.parent), [visibleItems]);

  const hrefOf = (href) => (isExternal(href) ? href : withLocaleHref(href, locale));

  return (
    <div
      onClick={toggle}
      className={`fixed left-0 top-0 z-50 h-[calc(100vh-56px)] w-full bg-black/50 transition-opacity duration-300 ${isOpen ? "opacity-100" : "pointer-events-none opacity-0"}`}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        // 메뉴(≡) 버튼이 상단 바 맨 오른쪽이라, 패널도 오른쪽에서 나오고 오른쪽으로 들어간다. 닫기(X)도 같은 자리
        className={`ml-auto flex h-full max-w-[500px] flex-col gap-5 bg-white transform transition-transform duration-300 ease-in-out ${isOpen ? "translate-x-0" : "translate-x-full"}`}
      >
        <div className="flex items-center justify-end bg-[#edebe9]">
          <button onClick={toggle} className="p-4" aria-label="닫기">
            <X />
          </button>
        </div>

        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-10 pb-8" aria-busy={isLoading}>
          {rootItems.length === 0 && (
            <div className="py-8 text-sm text-gray-600" role={loadError ? "alert" : "status"}>
              <p>{isLoading ? ui.loading : loadError ? ui.error : ui.empty}</p>
              {loadError && (
                <button type="button" onClick={reload} className="mt-4 rounded-lg border border-gray-300 px-4 py-2 text-gray-900">
                  {ui.retry}
                </button>
              )}
            </div>
          )}
          {rootItems.map((item) => {
            const children = childItems.filter((c) => c.parent === item.id);
            const title = labelOf(item.label, locale);

            if (children.length) {
              return (
                <button
                  key={item.id}
                  onClick={() => handleDropdown(item.id)}
                  className="w-full border-b border-[#ffc0cb] py-4 text-left"
                >
                  <div className="flex items-center justify-between">
                    <p className="ml-2 text-base font-medium">{title}</p>
                    <ChevronDown
                      className={`transform transition-transform duration-300 ${isDropdown === item.id ? "rotate-180" : ""}`}
                    />
                  </div>
                  <div
                    onClick={(e) => e.stopPropagation()}
                    className={`overflow-hidden transition-all duration-300 ${isDropdown === item.id ? "mt-3 max-h-40 opacity-100" : "max-h-0 opacity-0"}`}
                  >
                    <div className="flex flex-col">
                      {children.map((sub) => {
                        const subLabel = labelOf(sub.label, locale);
                        const href = hrefOf(sub.href);
                        const linkClass = "ml-4 py-1 text-sm font-medium text-[#808080]";

                        return isExternal(href) ? (
                          <a
                            key={sub.id}
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={linkClass}
                            onClick={closeSidebar}
                          >
                            {subLabel}
                          </a>
                        ) : (
                          <Link key={sub.id} href={href} className={linkClass} onClick={closeSidebar}>
                            {subLabel}
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                </button>
              );
            }

            const href = hrefOf(item.href);

            if (branchId && isPriceMenu(item.href)) {
              const open = isDropdown === item.id;
              const subs = [
                { id: "all", label: priceUi.all, href: "/clinicPrice" },
                ...(priceCats || []).map((c) => ({
                  id: c.id,
                  label: pickText(c.shortName ?? c.shortLabel ?? c.name, locale),
                  href: `/clinicPrice?catId=${encodeURIComponent(c.id)}&category=${encodeURIComponent(pickText(c.name, "ko"))}`,
                })),
              ];
              return (
                <div key={item.id} className="w-full border-b border-[#ffc0cb] py-4 text-left">
                  <div className="flex items-center justify-between">
                    <Link href={href} className="ml-2 flex-1 text-base font-medium" onClick={closeSidebar}>
                      {title}
                    </Link>
                    <button
                      type="button"
                      aria-expanded={open}
                      aria-label={priceUi.toggle}
                      onClick={() => {
                        if (!open) loadPriceCats();
                        handleDropdown(item.id);
                      }}
                      className="-my-2 -mr-2 p-2"
                    >
                      <ChevronDown className={`transform transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
                    </button>
                  </div>
                  {open && (
                    <div className="mt-3 flex flex-col">
                      {!priceCats && (
                        <p className="ml-4 py-1 text-sm text-[#b0b0b0]" role="status">
                          {priceCatsFailed ? ui.error : ui.loading}
                        </p>
                      )}
                      {subs.map((sub) => (
                        <Link
                          key={sub.id}
                          href={hrefOf(sub.href)}
                          className="ml-4 py-1.5 text-sm font-medium text-[#808080]"
                          onClick={closeSidebar}
                        >
                          {sub.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            }

            return isExternal(href) ? (
              <a
                key={item.id}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full border-b border-[#ffc0cb] py-4 text-left"
                onClick={closeSidebar}
              >
                <p className="ml-2 text-base font-medium">{title}</p>
              </a>
            ) : (
              <Link key={item.id} href={href} className="w-full border-b border-[#ffc0cb] py-4 text-left" onClick={closeSidebar}>
                <p className="ml-2 text-base font-medium">{title}</p>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default MobileSidebar;
